import { mapService } from "./mapService";
import store from "@/store.js";
import { fromLonLat } from "ol/proj";
import DragZoom from "ol/interaction/DragZoom";
import { mouseActionButton } from "ol/events/condition";
import { Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { Style, Icon } from "ol/style";

const LOCATION_PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
  <path d="M16 0C7.163 0 0 7.163 0 16c0 11 16 26 16 26s16-15 16-26C32 7.163 24.837 0 16 0z" fill="#e8730c" stroke="#ffffff" stroke-width="2"/>
  <circle cx="16" cy="16" r="6" fill="#ffffff"/>
</svg>`;
const LOCATION_PIN_ICON = "data:image/svg+xml;base64," + btoa(LOCATION_PIN_SVG);

class MapCustomControlsService {

    constructor() {
        this.boxZoomInteraction = null;
        this.boxZoomFinished = null;
        this.boxZoomTarget = null;
        this.previousMapCursor = "";
        this.geolocationLayer = null;
    }

    getGeolocationLayer(map) {
        if (this.geolocationLayer) return this.geolocationLayer;

        this.geolocationLayer = new VectorLayer({
            source: new VectorSource(),
            zIndex: 10000,
            style: new Style({
                image: new Icon({
                    src: LOCATION_PIN_ICON,
                    anchor: [0.5, 1],
                    scale: 1,
                }),
            }),
        });
        map.addLayer(this.geolocationLayer);
        return this.geolocationLayer;
    }

    zoomIn() {
        console.log("[MapCustomControlsService] zoomIn - Aumentando zoom");
        const map = mapService.get();
        if (!map) {
            console.error("[MapCustomControlsService] zoomIn - No hay mapa disponible");
            return;
        }
        const view = map.getView();
        const newZoom = view.getZoom() + 1;
        view.animate({ zoom: newZoom, duration: 150 });
        console.log(`[MapCustomControlsService] zoomIn - Nuevo zoom: ${newZoom}`);
    }

    zoomOut() {
        console.log("[MapCustomControlsService] zoomOut - Disminuyendo zoom");
        const map = mapService.get();
        if (!map) {
            console.error("[MapCustomControlsService] zoomOut - No hay mapa disponible");
            return;
        }
        const view = map.getView();
        const newZoom = view.getZoom() - 1;
        view.animate({ zoom: newZoom, duration: 150 });
        console.log(`[MapCustomControlsService] zoomOut - Nuevo zoom: ${newZoom}`);
    }

    resetView() {
        const map = mapService.get();
        if (!map) return;

        const region = store.getters.getUserRegion;

        if (region) {
            mapService.zoomToRegionExtent(region, { duration: 400 });
        } else {
            mapService.zoomToNational({ duration: 400 });
        }
    }

    locate(onSuccess, onError) {
        const map = mapService.get();

        if (!map) {
            if (onError) onError('No hay mapa disponible.');
            return;
        }

        if (!navigator.geolocation) {
            if (onError) onError('Tu navegador no soporta geolocalización.');
            return;
        }

        // Sin origen seguro (HTTPS o localhost) el navegador rechaza la geolocalización con
        // código 1 = "permiso denegado", que manda a revisar permisos en vano. Avisar la causa real.
        if (!window.isSecureContext) {
            if (onError) onError('La geolocalización requiere HTTPS. Esta página se sirve por HTTP, el navegador la bloquea.');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const coords = [pos.coords.longitude, pos.coords.latitude];
                const transformed = fromLonLat(coords);
                map.getView().animate({ center: transformed, zoom: 14, duration: 800 });

                const layer = this.getGeolocationLayer(map);
                const source = layer.getSource();
                source.clear();
                source.addFeature(new Feature(new Point(transformed)));

                if (onSuccess) onSuccess();
            },
            (error) => {
                console.error("[MapCustomControlsService] locate - Error:", error.message);
                const messages = {
                    1: 'Permiso denegado. Revisa los permisos del navegador, o verifica que la página use HTTPS.',
                    2: 'No se pudo determinar tu ubicación.',
                    3: 'Tiempo agotado al obtener la ubicación.',
                };
                if (onError) onError(messages[error.code] || 'Error al obtener la ubicación.');
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    }

    clearGeolocation() {
        if (this.geolocationLayer) {
            this.geolocationLayer.getSource().clear();
        }
    }

    toggleFullscreen() {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    }

    activateBoxZoom(onFinished) {
        const map = mapService.get();
        if (!map) return false;

        this.deactivateBoxZoom();
        this.boxZoomFinished = typeof onFinished === "function" ? onFinished : null;
        this.boxZoomInteraction = new DragZoom({
            condition: mouseActionButton,
            duration: 200,
            className: "ol-dragzoom"
        });

        const finish = () => this.finishBoxZoom();
        this.boxZoomInteraction.once("boxend", finish);
        this.boxZoomInteraction.once("boxcancel", finish);
        map.addInteraction(this.boxZoomInteraction);

        this.boxZoomTarget = map.getTargetElement();
        if (this.boxZoomTarget) {
            this.previousMapCursor = this.boxZoomTarget.style.cursor;
            this.boxZoomTarget.style.cursor = "crosshair";
        }

        return true;
    }

    finishBoxZoom() {
        const onFinished = this.boxZoomFinished;
        this.deactivateBoxZoom();
        if (onFinished) onFinished();
    }

    deactivateBoxZoom() {
        const map = mapService.get();
        if (map && this.boxZoomInteraction) {
            map.removeInteraction(this.boxZoomInteraction);
        }

        if (this.boxZoomTarget) {
            this.boxZoomTarget.style.cursor = this.previousMapCursor;
        }

        this.boxZoomInteraction = null;
        this.boxZoomFinished = null;
        this.boxZoomTarget = null;
        this.previousMapCursor = "";
    }
}

export const mapCustomControlsService = new MapCustomControlsService();
